import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, User, ArrowRight, ShieldCheck } from "lucide-react";
import { journeyApi, apiError } from "@/lib/journeyApi";
import { useAuth, destinationFor } from "@/context/AuthContext";
import { JourneyTopBar, JourneyFooter } from "@/components/JourneyChrome";
import { PasswordField } from "@/components/PasswordField";

const field = "w-full rounded-xl pe-surface py-3 pl-10 pr-3 text-[15px] text-[#171717] outline-none transition-colors focus:border-[#2F6B57] focus:ring-2 focus:ring-[#2F6B57]/25";

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { registerEmail, linkPurchase, googleRedirect } = useAuth();
  const sessionId = params.get("session_id");

  const [ready, setReady] = useState(false);
  const [method, setMethod] = useState("email"); // email | google
  const [form, setForm] = useState({ first: "", last: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!sessionId) { toast.error("Start by purchasing The Paycheck Edit."); navigate("/", { replace: true }); return; }
      try {
        const s = await journeyApi.activationSession(sessionId);
        if (!s.paid) { toast.error("We couldn't confirm your purchase."); navigate("/", { replace: true }); return; }
        if (s.already_activated) { toast("This purchase is already activated. Please sign in."); navigate("/login", { replace: true }); return; }
        setForm((f) => ({ ...f, email: s.email || "" }));
        setReady(true);
      } catch {
        toast.error("Something went wrong. Please try again.");
        navigate("/", { replace: true });
      }
    })();
  }, [sessionId, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const activateEmail = async () => {
    if (!form.first.trim() || !form.last.trim()) return toast.error("Please add your first and last name.");
    if (!form.email.trim()) return toast.error("Email is required.");
    if (form.password.length < 8) return toast.error("Use a password with at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      await registerEmail({
        email: form.email.trim().toLowerCase(), password: form.password,
        first_name: form.first.trim(), last_name: form.last.trim(),
      });
      const me = await linkPurchase(sessionId);
      toast.success("Your Paycheck Edit is active.");
      navigate(destinationFor(me) || "/onboarding", { replace: true });
    } catch (e) {
      const msg = apiError(e, "Could not activate your account.");
      if (e?.response?.status === 409) {
        toast.error("An account with this email already exists. Please sign in to link this purchase.");
        navigate("/login", { replace: true });
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  const activateGoogle = () => {
    // Carry the paid Stripe session through the OAuth redirect so it can be
    // linked to the authenticated identity when the customer returns.
    googleRedirect("activate", sessionId);
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F4]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F4] text-[#171717]" data-testid="activate-page">
      <JourneyTopBar showSignIn={false} />
      <main className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <div className="text-center">
            <span className="mx-auto flex min-h-12 w-12 items-center justify-center rounded-full bg-[#DDE7DF] text-[#164C3B]">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <h1 className="mt-5 font-serif text-3xl text-[#171717]">Activate your account</h1>
            <p className="mt-2 text-[15px] text-[#525252]">Purchase confirmed. Create your login to begin.</p>
          </div>

          <div className="mt-6 rounded-2xl pe-surface p-6">
            <div className="grid grid-cols-2 gap-2" data-testid="activate-method-toggle">
              {[["email", "Email + password"], ["google", "Continue with Google"]].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMethod(v)}
                  data-testid={`method-${v}`}
                  className={`rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${method === v ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]" : "border-[#E5E0D9] bg-white text-[#70584A] hover:bg-[#F5F2EC]"}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {method === "email" ? (
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A89A88]" />
                    <input data-testid="activate-first-name" value={form.first} onChange={set("first")} placeholder="First name" className={field} />
                  </div>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A89A88]" />
                    <input data-testid="activate-last-name" value={form.last} onChange={set("last")} placeholder="Last name" className={field} />
                  </div>
                </div>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A89A88]" />
                  <input data-testid="activate-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={field} />
                </div>
                <PasswordField testid="activate-password" value={form.password} onChange={set("password")} placeholder="Create a password (8+ characters)" autoComplete="new-password" />
                <PasswordField testid="activate-confirm" value={form.confirm} onChange={set("confirm")} placeholder="Confirm password" autoComplete="new-password" />
                <button
                  type="button"
                  onClick={activateEmail}
                  disabled={busy}
                  data-testid="activate-submit"
                  className="group mt-1 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[#2F6B57] px-6 text-base font-semibold text-white transition-all duration-200 hover:bg-[#255544] disabled:opacity-60"
                >
                  {busy ? "Activating…" : "Activate My The Paycheck Edit"}
                  {!busy && <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />}
                </button>
              </div>
            ) : (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={activateGoogle}
                  disabled={busy}
                  data-testid="activate-google"
                  className="inline-flex min-h-[52px] w-full items-center justify-center gap-3 rounded-full pe-surface px-6 text-base font-semibold text-[#171717] transition-colors hover:bg-[#F5F2EC] disabled:opacity-60"
                >
                  <GoogleGlyph /> Continue with Google
                </button>
                <p className="mt-3 text-center text-xs text-[#8A7C6B]">
                  We'll link your purchase to your Google account when you return.
                </p>
              </div>
            )}
          </div>

          <p className="mt-5 text-center text-xs text-[#8A7C6B] sm:whitespace-nowrap">
            We only collect your name and email, nothing else. Your payment stays with Stripe.
          </p>
          <p className="mt-2 text-center text-xs text-[#8A7C6B]">
            Already activated? <Link to="/login" className="font-semibold text-[#2F6B57] hover:underline">Sign in</Link>
          </p>
        </div>
      </main>
      <JourneyFooter />
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
